import React, { useState } from 'react';
import { useAnimalFollowupUpdates, useLoadMoreSentinel } from '@rehome/shared';
import type { AnimalFollowupUpdate } from '@rehome/shared';
import { HeartHandshake, Loader2, ChevronRight, ShieldCheck, MapPin, Smile, Meh, Frown } from 'lucide-react';
import { CheckInDetailDialog } from './CheckInDetailDialog';

interface AnimalCheckInSectionProps {
  animalId: string;
  animalName: string;
  adopterName?: string | null;
}

const TYPE_LABELS: Record<string, string> = {
  '3_Day': '3 napos',
  '3_Week': '3 hetes',
  '3_Month': '3 hónapos',
  Spontaneous: 'Spontán',
};

const MOOD_ICONS: Record<string, React.ReactNode> = {
  Happy: <Smile className="w-3.5 h-3.5 text-green-600" />,
  Neutral: <Meh className="w-3.5 h-3.5 text-gray-400" />,
  Struggling: <Frown className="w-3.5 h-3.5 text-orange-600" />,
};

/**
 * Az állathoz tartozó utókövetési helyzetjelentések az adatlapon.
 *
 * Az Utókövetés fülön egy régebbi örökbefogadás sorai idővel kicsúsznak a
 * látókörből (a lista a legutóbbi örökbefogadásokkal kezdődik) — a jelentések
 * viszont az állathoz tartoznak, itt kell megtalálhatónak lenniük.
 *
 * Ugyanúgy kurzorosan lapoz, mint a listás nézet: egy régóta követett állatnak
 * is lehet sok bejegyzése, és nem szeretnénk mindet egyszerre lehozni.
 */
export const AnimalCheckInSection: React.FC<AnimalCheckInSectionProps> = ({
  animalId, animalName, adopterName,
}) => {
  const { updates, loading, loadingMore, hasMore, loadMore, reload } = useAnimalFollowupUpdates(animalId);
  const sentinelRef = useLoadMoreSentinel(loadMore, hasMore && !loading);
  const [openUpdate, setOpenUpdate] = useState<AnimalFollowupUpdate | null>(null);

  // Örökbe nem adott állatnál nincs mit mutatni — a szekció ilyenkor el is
  // marad, nem üres dobozként áll az adatlapon.
  if (!loading && updates.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="p-6 border-b border-gray-100 flex items-center gap-2">
        <HeartHandshake className="w-5 h-5 text-teal-600" />
        <h2 className="text-lg font-bold text-gray-800">Utókövetési helyzetjelentések</h2>
        {!loading && (
          <span className="text-xs font-medium bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
            {updates.length}{hasMore ? '+' : ''}
          </span>
        )}
      </div>

      {loading && (
        <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
      )}

      {!loading && (
        <ul className="divide-y divide-gray-100">
          {updates.map(update => (
            <li key={update.id}>
              <button
                type="button"
                onClick={() => setOpenUpdate(update)}
                className="w-full text-left px-6 py-4 hover:bg-gray-50 transition-colors flex items-center gap-4 group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">
                      {TYPE_LABELS[update.updateType] || update.updateType}
                    </span>
                    <span className="text-xs text-gray-400">
                      {(update.createdAt || '').split('T')[0]}
                    </span>
                    {update.mood && MOOD_ICONS[update.mood]}
                    {update.gpsLat != null && update.gpsLng != null && (
                      <MapPin className="w-3.5 h-3.5 text-green-600" />
                    )}
                    {update.verifiedByShelter && (
                      <ShieldCheck className="w-3.5 h-3.5 text-green-600" />
                    )}
                  </div>
                  {update.notes && (
                    // Egy sorban, levágva: a teljes beszámoló a dialogban van.
                    <p className="text-sm text-gray-500 truncate mt-0.5">{update.notes}</p>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-teal-600 transition-colors flex-shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {hasMore && (
        <div ref={sentinelRef} className="py-4 flex items-center justify-center text-xs text-gray-400 gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> További jelentések…
        </div>
      )}
      {loadingMore && !hasMore && (
        <div className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>
      )}

      <CheckInDetailDialog
        update={openUpdate}
        animalName={animalName}
        adopterName={adopterName}
        onClose={() => setOpenUpdate(null)}
        onVerified={reload}
      />
    </div>
  );
};
